import React from 'react';
import { Step, Question } from '../../types';

interface StepSidebarProps {
  steps: Step[];
  questions: Question[];
  activeStepId: number;
  onStepSelect: (stepNumber: number) => void;
  // Client-specific props
  isClientView?: boolean;
  userResponses?: Array<{
    id: number;
    user_id: number;
    template_id: number;
    question_id: number;
    response_text: string;
    interval: 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly';
    created_at: string;
    updated_at: string;
  }>;
}

const StepSidebar: React.FC<StepSidebarProps> = ({
  steps,
  questions,
  activeStepId,
  onStepSelect,
  isClientView = false,
  userResponses = []
}) => {
  // Count questions for each step
  const getQuestionCount = (stepNumber: number): number => {
    return questions.filter(q => q.step_number === stepNumber).length;
  };

  // Get progress information for client users
  const getStepProgress = (stepNumber: number) => {
    const stepQuestions = questions.filter(q => q.step_number === stepNumber);
    const completedQuestions = stepQuestions.filter(q => {
      return userResponses.some(r => r.question_id === q.id && r.response_text.trim() !== '');
    });
    
    return {
      completed: completedQuestions.length,
      total: stepQuestions.length,
      percentage: stepQuestions.length > 0 ? Math.round((completedQuestions.length / stepQuestions.length) * 100) : 0
    };
  };

  // Predefined gradients for steps (matching KanbanBoard)
  // const stepGradients = [
  //   'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', // Step 1 - Purple/Blue
  //   'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', // Step 2 - Pink/Red
  //   'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', // Step 3 - Blue/Cyan
  //   'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', // Step 4 - Green/Teal
  //   'linear-gradient(135deg, #fa709a 0%, #fee140 100%)'  // Step 5 - Pink/Yellow
  // ];

  const stepGradients = [
    '#DFF4D8', // Light green
    '#CAEDBD', // Lighter green
    '#9EDD87', // Medium green
    '#7DD35F', // Darker green
    '#58BE34', // Dark green
  ];

  // Function to determine text color based on background color
  const getTextColor = (stepNumber: number): string => {
    // For the lighter greens (steps 1-3), use dark text
    // For the darker greens (steps 4-5), use white text
    return stepNumber <= 3 ? '#1F2937' : '#FFFFFF'; // gray-800 : white
  };

  const getStepGradient = (stepNumber: number): string => {
    return stepGradients[stepNumber - 1] || stepGradients[0];
  };

  return (
    <div className="w-64 bg-white border border-gray-200 flex flex-col">
      {/* Sidebar Header */}
      <div className="p-4 border-b border-gray-200 bg-gray-50">
        <h2 className="text-md font-semibold text-gray-600">Template Steps</h2>
        <p className="text-sm text-gray-500 mt-1">
          Click a step to edit questions
        </p>
      </div>

      {/* Steps List */}
      <div className="flex-1">
        <div>
          {steps.map((step) => {
            const questionCount = getQuestionCount(step.step_number);
            const progress = getStepProgress(step.step_number);
            const isActive = step.step_number === activeStepId;
            
            return (
              <button
                key={step.id}
                onClick={() => onStepSelect(step.step_number)}
                className={`w-full text-left p-3 transition-all duration-200 border-b py-6 ${
                  isActive
                    ? 'bg-secondary-50 border-l-4 border-l-secondary-500 border-r-4 border-r-secondary-500'
                    : 'hover:bg-gray-100 border-l-4 border-l-transparent border-r-4 border-r-transparent'
                }`}
              >
                <div className="flex items-center space-x-3">
                  {/* Step Number Circle */}
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0"
                    style={{ 
                      background: getStepGradient(step.step_number),
                      color: getTextColor(step.step_number)
                    }}
                  >
                    {step.step_number}
                  </div>
                  
                  {/* Step Info */}
                  <div className="flex-1 min-w-0">
                    <h3 className={`font-medium text-sm truncate ${
                      isActive ? 'text-secondary-900' : 'text-gray-900'
                    }`}>
                      {step.step_name}
                    </h3>
                    <div className="flex items-center mt-2">
                      {isClientView ? (
                        /* Client View - Progress Badge */
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          progress.percentage === 0 
                            ? 'bg-gray-100 text-gray-600'
                            : progress.percentage === 100 
                            ? 'bg-green-100 text-green-800'
                            : 'bg-orange-100 text-orange-800'
                        }`}>
                          {progress.percentage === 100 && (
                            <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                          {progress.completed}/{progress.total} completed
                        </span>
                      ) : (
                        /* Admin View - Question Count */
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          isActive 
                            ? 'bg-secondary-100 text-secondary-800'
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          {questionCount} {questionCount === 1 ? 'question' : 'questions'}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Active Indicator */}
                  {isActive && (
                    <div className="flex-shrink-0">
                      <svg className="w-5 h-5 text-secondary-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Sidebar Footer */}
      <div className="p-4 border-t border-gray-200">
        <div className="text-center">
          {isClientView ? (
            /* Client View - Overall Progress */
            (() => {
              const totalQuestions = questions.length;
              const completedQuestions = questions.filter(q => {
                return userResponses.some(r => r.question_id === q.id && r.response_text.trim() !== '');
              }).length;
              const overallPercentage = totalQuestions > 0 ? Math.round((completedQuestions / totalQuestions) * 100) : 0;
              
              return (
                <p className="text-xs text-gray-500">
                  Overall Progress: {completedQuestions}/{totalQuestions} ({overallPercentage}%)
                </p>
              );
            })()
          ) : (
            /* Admin View - Question Count */
            <p className="text-xs text-gray-500">
              Total: {questions.length} {questions.length === 1 ? 'question' : 'questions'}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default StepSidebar;